import { useEffect, useRef, useState } from 'react';
import { getCurrentMonthYear } from '../../../helpers/utils/utils';
import type { DrawCountdown } from '../../Rewards/RewardsPage/rewardsPageTypes';
import { HERO_PARTICLES } from '../prizeDrawConstants';
import {
  animateHeroTicketCount,
  CD_BOX_ENTRY_BASE_DELAY_S,
  CD_BOX_ENTRY_STAGGER_S,
} from '../utils/prizeDrawHeroUtils';

const pad2 = (n: number) => String(Math.max(0, n)).padStart(2, '0');

const COUNTDOWN_PARTS: Array<{ key: keyof DrawCountdown; label: string }> = [
  { key: 'days', label: 'days' },
  { key: 'hours', label: 'hrs' },
  { key: 'minutes', label: 'min' },
  { key: 'seconds', label: 'sec' },
];

type PrizeDrawHeroProps = {
  tickets: number;
  countdown: DrawCountdown;
  onEarnTickets: () => void;
  onProgramRules: () => void;
  onOpenTerms: () => void;
};

export const PrizeDrawHero = ({
  tickets,
  countdown,
  onEarnTickets,
  onProgramRules,
  onOpenTerms,
}: PrizeDrawHeroProps) => {
  const ticketsRef = useRef<HTMLDivElement>(null);
  const displayedTicketsRef = useRef(0);
  const [displayedTickets, setDisplayedTickets] = useState(0);

  useEffect(() => {
    const el = ticketsRef.current;
    if (!el) return undefined;

    const from = displayedTicketsRef.current;

    return animateHeroTicketCount(el, from, tickets, (value) => {
      displayedTicketsRef.current = value;
      setDisplayedTickets(value);
    });
  }, [tickets]);

  return (
    <div className='hero'>
      <div className='hero-glow-wm hero-glow-gold' aria-hidden='true'></div>
      <div className='hero-orb orb-gold'></div>
      {HERO_PARTICLES.map((particle) => (
        <div
          key={`${particle.top}-${particle.left}`}
          className={`sv-particle ${particle.tone}`}
          style={{
            top: particle.top,
            left: particle.left,
            width: particle.width,
            height: particle.height,
            animationDelay: particle.delay,
            animationDuration: particle.duration,
          }}
          aria-hidden='true'
        />
      ))}
      <div className='hero-body' style={{ padding: 1 }}>
        <div className='hero-l' style={{ padding: '0 0 0 3px' }}>
          <div className='hero-meta'>
            <div className='badge badge-live'>
              <div className='bdot' />
              <span className='badge-live-txt'>Live</span>
            </div>
            <div className='badge badge-neutral'>{getCurrentMonthYear()}</div>
          </div>
          <h1 className='hero-h1'>
            <span style={{ color: '#f5a623' }}>This month&apos;s drop is live.</span>
            <br />
            <em>Your rent could be on us.</em>
          </h1>
          <p className='hero-p'>
            Every month, we share a portion of our revenue with members who pay rent with TenantPay
            between the 7th and 28th - giving you the chance to receive rent savings ranging from
            partial savings to a full month&apos;s rent. It doesn&apos;t get better than that.{' '}
            <a
              role='button'
              tabIndex={0}
              onClick={onOpenTerms}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onOpenTerms();
                }
              }}
            >
              See full program terms.
            </a>
          </p>
          <div className='hero-ctas'>
            <button type='button' className='btn-gold' onClick={onEarnTickets}>
              Earn tickets
            </button>
            <button type='button' className='btn-ghost' onClick={onProgramRules}>
              Program rules
            </button>
          </div>
        </div>
        <div className='hero-r'>
          <div className='tix-block'>
            <div className='tix-top'>
              <div className='tix-lbl'>Drop tickets</div>
            </div>
            <div ref={ticketsRef} className='tix-num' data-value={tickets}>
              {displayedTickets.toLocaleString()}
            </div>
            <div className='tix-sub'>tickets this month</div>
          </div>
          <div>
            <div className='cd-lbl'>Drop closes in</div>
            <div className='cd-row'>
              {COUNTDOWN_PARTS.map((part, index) => (
                <div key={part.key} style={{ display: 'contents' }}>
                  {index > 0 ? <div className='cdsep'>:</div> : null}
                  <div
                    className='cdbox is-entering'
                    style={{
                      animationDelay: `${CD_BOX_ENTRY_BASE_DELAY_S + index * CD_BOX_ENTRY_STAGGER_S}s`,
                    }}
                  >
                    <span className='cdnum'>{pad2(countdown[part.key])}</span>
                    <div className='cdsub'>{part.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
